// components/Dashboard/Dashboard.tsx
import React, { useState, useEffect, useMemo } from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { Sidebar } from './Sidebar';
import { Header } from './Header';
import { InspectionList } from './InspectionList';
import { Settings } from './Settings';
import { inspectionService } from '../../services/inspectionService';
import { authService } from '../../services/authService';
import { Inspection, InspectionStat } from '../../types/inspection';
import { Messages } from "./Messages";
import { Analytics } from './Analytics';
import { Reviews } from "./Reviews";
import { AIAssistant } from './AIAssistant';
import { useLanguage } from '../../hooks/useLanguage';
import { GarageFinances } from "./GarageFinances";
import { AppointmentsPage } from '../Appointments/AppointmentsPage';
import { TodayAppointmentsWidget } from '../Appointments/TodayAppointmentsWidget';

export const Dashboard = () => {
    const { t, isRTL } = useLanguage();
    const navigate = useNavigate();
    const [inspections, setInspections] = useState<Inspection[]>([]);
    const [creditBalance, setCreditBalance] = useState<{ standard: number; ai: number } | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => { loadData(); }, []);

    const loadData = async () => {
        try {
            setLoading(true);
            setError(null);

            if (!authService.isAuthenticated()) {
                navigate('/login');
                return;
            }

            const data = await inspectionService.getAllInspectionsByGarageId();
            const user = JSON.parse(localStorage.getItem('user') || '{}');
            const garageId = user._id;

            if (!garageId) throw new Error(t('dashboard.errors.garageIdMissing'));

            await inspectionService.getGarageStatsInspections(garageId);
            setInspections(data);
        } catch (err: any) {
            console.error('Erreur lors du chargement des données:', err);
            if (err.response && (err.response.status === 401 || err.response.status === 403)) {
                authService.logout();
                navigate('/login');
                setError(t('dashboard.errors.sessionExpired'));
            } else {
                setError(err.message || t('dashboard.errors.loadingInspections'));
            }
        } finally {
            setLoading(false);
        }
    };

    const handleStatusChange = async (id: string, status: string) => {
        try {
            await inspectionService.updateInspectionStatus(id, status);
            loadData();
        } catch (err) {
            setError(t('dashboard.errors.statusUpdate'));
        }
    };

    const handleSearch = (term: string) => { setSearchTerm(term); };

    const filterInspections = (inspectionsToFilter: Inspection[]) => {
        if (!searchTerm.trim()) return inspectionsToFilter;
        const searchLower = searchTerm.toLowerCase();
        return inspectionsToFilter.filter(inspection =>
            inspection.clientName?.toLowerCase().includes(searchLower) ||
            inspection.vehicleBrand?.toLowerCase().includes(searchLower) ||
            inspection.vehicleModel?.toLowerCase().includes(searchLower) ||
            inspection.vehicleYear?.toString().includes(searchTerm) ||
            inspection.clientPhone?.includes(searchTerm) ||
            inspection.description?.toLowerCase().includes(searchLower) ||
            inspection.location?.address?.toLowerCase().includes(searchLower)
        );
    };

    const pendingInspections    = useMemo(() => filterInspections(inspections.filter(i => i.status === 'pending')), [inspections, searchTerm]);
    const urgentInspections     = useMemo(() => filterInspections(inspections.filter(i => i.urgency === 'high')), [inspections, searchTerm]);
    const inProgressInspections = useMemo(() => filterInspections(inspections.filter(i => i.status === 'in_progress' || i.status === 'accepted')), [inspections, searchTerm]);
    const completedInspections  = useMemo(() => filterInspections(inspections.filter(i => i.status === 'completed')), [inspections, searchTerm]);

    if (loading) {
        return (
            <div className="flex h-screen items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
            </div>
        );
    }

    if (error && authService.isAuthenticated()) {
        return (
            <div className={`flex h-screen items-center justify-center flex-col ${isRTL ? 'rtl' : ''}`}>
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                    <p>{error}</p>
                </div>
                <button onClick={loadData} className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded">
                    {t('dashboard.actions.retry')}
                </button>
            </div>
        );
    }

    return (
        <div className="h-screen flex overflow-hidden" dir={isRTL ? 'rtl' : 'ltr'}>

            {/*
                Sidebar: sur desktop elle occupe sa largeur naturelle (w-64) dans le flux.
                Sur mobile elle est fixed (position absolute hors du flux) — pas de wrapper div ici.
            */}
            <Sidebar creditBalance={creditBalance} />

            {/* Contenu principal — prend tout l'espace restant sur desktop, tout l'écran sur mobile */}
            <div className="flex-1 flex flex-col overflow-hidden min-w-0">
                <Header onSearch={handleSearch} />

                <main className={`flex-1 overflow-y-auto bg-gray-100 p-3 sm:p-4 ${isRTL ? 'rtl' : ''}`}>
                    {error && (
                        <div className={`mb-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative flex justify-between items-center ${isRTL ? 'flex-row-reverse' : ''}`}>
                            <div>{error}</div>
                            <button onClick={() => setError(null)} className="text-red-700 hover:text-red-900" aria-label={t('dashboard.actions.close')}>&times;</button>
                        </div>
                    )}

                    {searchTerm && (
                        <div className={`mb-4 bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 rounded flex justify-between items-center ${isRTL ? 'flex-row-reverse' : ''}`}>
                            <p>{t('dashboard.search.results')} <span className="font-medium">"{searchTerm}"</span></p>
                            <button onClick={() => setSearchTerm('')} className="text-blue-700 hover:text-blue-900" aria-label={t('dashboard.search.clear')}>&times;</button>
                        </div>
                    )}

                    <Routes>
                        <Route path="/" element={<Navigate to="/dashboard/pending" replace />} />
                        <Route path="/pending"     element={<InspectionList inspections={pendingInspections}    title={t('dashboard.sections.pending')}    onStatusChange={handleStatusChange} onRefresh={loadData} />} />
                        <Route path="/urgent"      element={<InspectionList inspections={urgentInspections}    title={t('dashboard.sections.urgent')}     onStatusChange={handleStatusChange} onRefresh={loadData} />} />
                        <Route path="/in-progress" element={<InspectionList inspections={inProgressInspections} title={t('dashboard.sections.inProgress')} onStatusChange={handleStatusChange} onRefresh={loadData} />} />
                        <Route path="/completed"   element={<InspectionList inspections={completedInspections}  title={t('dashboard.sections.completed')}  onStatusChange={handleStatusChange} onRefresh={loadData} />} />
                        <Route path="/messages"    element={<Messages />} />
                        <Route path="/reviews"     element={<Reviews />} />
                        <Route path="/analytics"   element={<Analytics />} />
                        <Route path="/appointments" element={<AppointmentsPage />} />
                        <Route path="/finances"    element={<GarageFinances />} />
                        <Route path="/settings"    element={<Settings />} />
                    </Routes>
                </main>
            </div>

            {/* Widget RDV du jour — visible sur toutes les pages */}
            <TodayAppointmentsWidget />
        </div>
    );
};